import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import { supabase } from '../lib/supabase'
import TripFormModal from './TripFormModal'

const teal = '#0D9488'
const plum = '#1E1040'

/**
 * TripDetailModal
 * Props:
 *   trip — the mileage_trips row
 *   vehicles — list of vehicles for nickname display
 *   onClose
 *   onUpdated(row)
 *   onDeleted(id)
 */
export default function TripDetailModal({ trip, vehicles = [], onClose, onUpdated, onDeleted }) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape' && !editing) onClose() }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [onClose, editing])

  const handleDelete = async () => {
    setDeleting(true)
    setError('')
    try {
      const { error: err } = await supabase
        .from('mileage_trips')
        .delete()
        .eq('id', trip.id)
      if (err) throw err
      onDeleted?.(trip.id)
      onClose()
    } catch (err) {
      setError(err.message || t('trip_detail.error_delete'))
      setDeleting(false)
    }
  }

  const handleEditSaved = (updated) => {
    onUpdated?.(updated)
    setEditing(false)
  }

  if (editing) {
    return (
      <TripFormModal
        existingTrip={trip}
        onClose={() => setEditing(false)}
        onSaved={handleEditSaved}
      />
    )
  }

  // Locale-aware formatters
  const lang = i18n.language || 'en'
  const locale = lang.startsWith('es') ? 'es-US' : 'en-US'

  const formatCurrency = (n) => new Intl.NumberFormat(locale, {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(parseFloat(n || 0))

  const formatMiles = (n) => parseFloat(n || 0).toLocaleString(locale, { maximumFractionDigits: 1 })

  const formatDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString(locale, {
    month: 'long', day: 'numeric', year: 'numeric',
  })

  const vehicle = vehicles.find(v => v.id === trip.vehicle_id)

  return (
    <div style={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div style={styles.title}>{t('trip_detail.title')}</div>
          <button style={styles.closeBtn} onClick={onClose} aria-label={t('common.close')}>✕</button>
        </div>

        {/* Headline — deduction */}
        <div style={styles.hero}>
          <div style={styles.heroAmount}>{formatCurrency(trip.deduction_amount)}</div>
          <div style={styles.heroMiles}>
            {t('trip_detail.miles_summary', {
              miles: formatMiles(trip.miles),
              type: trip.round_trip ? t('trip_detail.round_trip_lower') : t('trip_detail.one_way_lower'),
            })}
          </div>
          <div style={styles.heroDate}>{formatDate(trip.trip_date)}</div>
        </div>

        {/* Route */}
        <div style={styles.routeBox}>
          <div style={styles.routeRow}>
            <div style={styles.routeBullet}>●</div>
            <div style={styles.routeText}>
              <div style={styles.routeLabel}>{t('trip_form.from_label')}</div>
              <div style={styles.routeAddress}>{trip.from_address}</div>
            </div>
          </div>
          <div style={styles.routeConnector}></div>
          <div style={styles.routeRow}>
            <div style={{ ...styles.routeBullet, color: teal }}>◉</div>
            <div style={styles.routeText}>
              <div style={styles.routeLabel}>{t('trip_form.to_label')}</div>
              <div style={styles.routeAddress}>{trip.to_address}</div>
            </div>
          </div>
        </div>

        {/* Meta */}
        <div style={styles.metaGrid}>
          {trip.purpose && (
            <div style={styles.metaRow}>
              <span style={styles.metaLabel}>{t('trip_form.purpose_label')}</span>
              <span style={styles.metaValue}>{trip.purpose}</span>
            </div>
          )}
          {vehicle && (
            <div style={styles.metaRow}>
              <span style={styles.metaLabel}>{t('trip_form.vehicle_label')}</span>
              <span style={styles.metaValue}>{vehicle.nickname || vehicle.make_model || t('trip_form.vehicle_default_name')}</span>
            </div>
          )}
          <div style={styles.metaRow}>
            <span style={styles.metaLabel}>{t('trip_detail.irs_rate')}</span>
            <span style={styles.metaValue}>{t('trip_detail.rate_per_mile', { rate: (trip.rate_used_cents / 100).toFixed(2) })}</span>
          </div>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {!confirmDelete ? (
          <div style={styles.actions}>
            <button style={styles.btnDanger} onClick={() => setConfirmDelete(true)}>{t('common.delete')}</button>
            <button style={styles.btnPrimary} onClick={() => setEditing(true)}>{t('common.edit')}</button>
          </div>
        ) : (
          <div style={styles.confirmBlock}>
            <div style={styles.confirmText}>{t('trip_detail.delete_confirm')}</div>
            <div style={styles.actions}>
              <button style={styles.btnSecondary} onClick={() => setConfirmDelete(false)}>{t('common.cancel')}</button>
              <button style={styles.btnDangerSolid} onClick={handleDelete} disabled={deleting}>
                {deleting ? t('trip_detail.deleting') : t('trip_detail.confirm_delete')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 2000,
    background: 'rgba(15, 23, 42, 0.75)',
    backdropFilter: 'blur(6px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px',
  },
  modal: {
    background: '#fff', borderRadius: '20px', padding: '24px',
    width: '100%', maxWidth: '520px', maxHeight: '92vh', overflowY: 'auto',
    boxShadow: '0 32px 80px rgba(0,0,0,0.3)',
    fontFamily: "'Plus Jakarta Sans', sans-serif", color: plum,
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '20px',
  },
  title: {
    fontFamily: "'Fraunces', serif", fontSize: '22px', fontWeight: 800,
    color: plum, letterSpacing: '-0.3px',
  },
  closeBtn: {
    background: '#F3F4F6', border: 'none', borderRadius: '50%',
    width: '36px', height: '36px', fontSize: '14px', color: '#6B7280',
    cursor: 'pointer', fontWeight: 700,
  },
  hero: { marginBottom: '20px' },
  heroAmount: {
    fontFamily: "'Fraunces', serif", fontSize: '38px', fontWeight: 900,
    color: plum, letterSpacing: '-1px', lineHeight: 1, marginBottom: '6px',
  },
  heroMiles: { fontSize: '15px', fontWeight: 700, color: teal, marginBottom: '4px' },
  heroDate: { fontSize: '14px', color: '#6B7280', fontWeight: 500 },
  routeBox: {
    background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '12px',
    padding: '14px 18px', marginBottom: '16px',
  },
  routeRow: { display: 'flex', gap: '12px', alignItems: 'flex-start' },
  routeBullet: {
    fontSize: '14px', color: '#9CA3AF', marginTop: '2px', flexShrink: 0, width: '14px',
  },
  routeText: { flex: 1, minWidth: 0 },
  routeLabel: {
    fontSize: '11px', fontWeight: 700, color: '#9CA3AF',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px',
  },
  routeAddress: { fontSize: '14px', color: plum, fontWeight: 500, lineHeight: 1.4 },
  routeConnector: {
    width: '2px', height: '14px', background: '#E5E7EB',
    marginLeft: '6px', marginTop: '4px', marginBottom: '4px',
  },
  metaGrid: {
    background: '#F9FAFB', borderRadius: '12px', padding: '10px 18px',
    marginBottom: '16px',
  },
  metaRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '8px 0', borderBottom: '1px solid #EEF2F7', fontSize: '14px',
  },
  metaLabel: { color: '#6B7280', fontWeight: 500 },
  metaValue: { color: plum, fontWeight: 600, textAlign: 'right' },
  error: {
    background: '#FEE2E2', color: '#991B1B',
    padding: '10px 14px', borderRadius: '8px',
    fontSize: '13px', marginBottom: '12px', fontWeight: 500,
  },
  actions: { display: 'flex', gap: '10px' },
  btnPrimary: {
    flex: 2, padding: '13px', background: teal, color: '#042f2e',
    border: 'none', borderRadius: '100px', fontSize: '14px', fontWeight: 700,
    cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxShadow: `0 4px 14px ${teal}40`,
  },
  btnSecondary: {
    flex: 1, padding: '13px', background: '#fff', color: plum,
    border: '1.5px solid #E5E7EB', borderRadius: '100px',
    fontSize: '14px', fontWeight: 600, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  btnDanger: {
    flex: 1, padding: '13px', background: '#fff', color: '#DC2626',
    border: '1.5px solid #FECACA', borderRadius: '100px',
    fontSize: '14px', fontWeight: 600, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  btnDangerSolid: {
    flex: 2, padding: '13px', background: '#DC2626', color: '#fff',
    border: 'none', borderRadius: '100px',
    fontSize: '14px', fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  confirmBlock: { marginTop: '4px' },
  confirmText: {
    background: '#FEF3C7', color: '#92400E',
    padding: '12px 16px', borderRadius: '10px',
    fontSize: '13px', fontWeight: 500, marginBottom: '12px',
  },
}
